import types from './types';
import { getCampaignTags, addCampaignTag, removeCampaignTag } from '@client/helpers/pushNotification/campaignTags';
import { canRegister, registerUser, getChannelId, isOptedIn, isSupported, getPermission } from '@client/helpers/pushNotification/management';
import { showHeaderErrorFlashMessage, showHeaderSuccessFlashMessage } from '@client/redux/modules/flashMessage/actions';
import { sendWebPushNotification } from '@client/services/api/pushNotification';
import { getToken } from '@client/redux/modules/user/selectors';

export const showPermissionDialog = () => ({
    type: types.SHOW_PERMISSION_DIALOG
});

export const hidePermissionDialog = () => ({
    type: types.HIDE_PERMISSION_DIALOG
});

export const showReenablementDialog = () => ({
    type: types.SHOW_REENABLEMENT_DIALOG
});

export const hideReenablementDialog = () => ({
    type: types.HIDE_REENABLEMENT_DIALOG
});

export const setCampaignToSubscribe = (campaignIdentifier) => ({
    type: types.SET_CAMPAIGN_TO_SUBSCRIBE,
    payload: campaignIdentifier
});

export const resetCampaignToSubscribe = () => ({
    type: types.RESET_CAMPAIGN_TO_SUBSCRIBE
});

export const fetchCampaignNotificationTags = (appdomainId) => async (dispatch) => {
    dispatch({
        type: types.FETCH_NOTIFICATION_TAGS.REQUEST
    });

    try {
        const tags = await getCampaignTags(appdomainId);
        dispatch({
            type: types.FETCH_NOTIFICATION_TAGS.SUCCESS,
            payload: { tags }
        });
    }
    catch (error) {
        dispatch({
            type: types.FETCH_NOTIFICATION_TAGS.ERROR,
            payload: error
        });
    }
};

export const addCampaignNotificationTag = (tag, appdomainId) => async (dispatch) => {
    dispatch({
        type: types.ADD_NOTIFICATION_TAG.REQUEST,
        payload: tag
    });

    try {
        const result = await addCampaignTag(tag, appdomainId);
        const tags = await getCampaignTags(appdomainId);

        dispatch({
            type: types.ADD_NOTIFICATION_TAG.SUCCESS,
            payload: { tags, selectedTag: tag }
        });

        return result;
    }
    catch (error) {
        dispatch({
            type: types.ADD_NOTIFICATION_TAG.ERROR,
            payload: tag
        });

        return null;
    }
};

export const removeCampaignNotificationTag = (tag, appdomainId) => async (dispatch) => {
    dispatch({
        type: types.REMOVE_NOTIFICATION_TAG.REQUEST,
        payload: tag
    });

    try {
        await removeCampaignTag(tag, appdomainId);
        const tags = await getCampaignTags(appdomainId);
        dispatch({
            type: types.REMOVE_NOTIFICATION_TAG.SUCCESS,
            payload: { tags, selectedTag: tag }
        });
    }
    catch (error) {
        dispatch({
            type: types.REMOVE_NOTIFICATION_TAG.ERROR,
            payload: tag
        });
    }
};

export const fetchRegistrationForNotification = () => async (dispatch) => {
    dispatch({
        type: types.CHECK_REGISTRATION.REQUEST
    });

    try {
        const registrationPossible = await canRegister();
        const clientChannelId = await getChannelId();
        dispatch({
            type: types.CHECK_REGISTRATION.SUCCESS,
            payload: {
                registrationPossible,
                clientChannelId
            }
        });
    }
    catch (error) {
        dispatch({
            type: types.CHECK_REGISTRATION.ERROR,
            payload: error
        });
    }
};

export const fetchNotificationPermission = () => async (dispatch) => {
    dispatch({
        type: types.CHECK_PERMISSION.REQUEST
    });

    try {
        const permissionStatus = await getPermission();
        dispatch({
            type: types.CHECK_PERMISSION.SUCCESS,
            payload: permissionStatus
        });
    }
    catch (error) {
        dispatch({
            type: types.CHECK_PERMISSION.ERROR,
            payload: error
        });
    }
};

export const sendPushNotification = (data) => async (dispatch, getState) => {
    dispatch({ type: types.SEND_PUSH.REQUEST });

    const state = getState();
    const optedIn = await isOptedIn();
    const clientChannelId = await getChannelId();
    const token = getToken(state);

    try {
        await sendWebPushNotification({
            clientChannelId: clientChannelId || 'invalid',
            token,
            data: {
                ...data,
                optedIn
            }
        });
        dispatch({ type: types.SEND_PUSH.SUCCESS });
    }
    catch (error) {
        dispatch({ type: types.SEND_PUSH.ERROR });
    }
};

export const fetchOptedInForNotification = () => async (dispatch) => {
    dispatch({
        type: types.CHECK_OPTED_IN.REQUEST
    });

    try {
        const optedIn = await isOptedIn();
        dispatch({
            type: types.CHECK_OPTED_IN.SUCCESS,
            payload: optedIn
        });
    }
    catch (error) {
        dispatch({
            type: types.CHECK_OPTED_IN.ERROR,
            payload: error
        });
    }
};

export const registerForNotification = ({ campaignToSubscribe, appdomainid, errorMessage, successMessage }) => async (dispatch) => {
    dispatch({ type: types.REGISTER.REQUEST });
    try {
        const result = await registerUser();
        if (campaignToSubscribe) {
            dispatch(addCampaignNotificationTag(campaignToSubscribe, appdomainid));
        }
        dispatch(fetchOptedInForNotification());
        dispatch(showHeaderSuccessFlashMessage(successMessage));
        dispatch({
            type: types.REGISTER.SUCCESS,
            payload: result
        });
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);

        const PERMISSION_DENIED = 'permission denied';
        if (error && error.message && !error.message.includes(PERMISSION_DENIED)) {
            dispatch(showHeaderErrorFlashMessage(errorMessage));
        }
        dispatch(fetchOptedInForNotification());
        dispatch(fetchNotificationPermission());
        dispatch({ type: types.REGISTER.ERROR, payload: error });
    }
};

export const fetchNotificationSupported = () => async (dispatch) => {
    dispatch({
        type: types.CHECK_SUPPORT.REQUEST
    });

    try {
        const notificationSupported = await isSupported();
        dispatch({
            type: types.CHECK_SUPPORT.SUCCESS,
            payload: notificationSupported
        });
    }
    catch (error) {
        dispatch({
            type: types.CHECK_SUPPORT.ERROR,
            payload: error
        });
    }
};
